"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  type ConstantState,
  initialConstant,
  readConstant,
  WEAK_CONSTANT_STRINGS,
  WeakConstantFields,
} from "@/components/tools/chem-reactions/weak-constant";
import {
  FORMULA_ERROR_STRINGS,
  formulaErrorMessage,
  WORKING_BOX,
} from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { pFrom } from "@/lib/tool-kit/chem-reactions/acid-base";
import {
  BUFFER_PAIRS,
  type BufferPair,
} from "@/lib/tool-kit/chem-reactions/acid-base-data";
import {
  approximationHolds,
  bufferPh,
  bufferRecipe,
  exactBufferPh,
  inBufferRange,
  ratioForPh,
  shares,
} from "@/lib/tool-kit/chem-reactions/buffer";
import {
  fewestSigFigs,
  formatFixed,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { molarMass, parseFormula } from "@/lib/tool-kit/chemistry/formula";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const HENDERSON_HASSELBALCH_CALCULATOR_STRINGS = {
  ...WEAK_CONSTANT_STRINGS,
  modeLabel: "What do you want to work out?",
  modePh: "pH of a buffer",
  modeRatio: "Ratio for a target pH",
  modeRecipe: "Recipe for a buffer",
  pairLabel: "Weak acid and its conjugate base",
  pairNames: {
    acetic: "Acetic acid / acetate",
    formic: "Formic acid / formate",
    carbonic: "Carbonic acid / hydrogen carbonate",
    bicarbonate: "Hydrogen carbonate / carbonate",
    dihydrogenPhosphate: "Dihydrogen phosphate / hydrogen phosphate",
    hydrogenPhosphate: "Hydrogen phosphate / phosphate",
    ammonium: "Ammonium / ammonia",
    hypochlorous: "Hypochlorous acid / hypochlorite",
  },
  acidConcentration: "Concentration of the acid, [HA] (mol/L)",
  baseConcentration: "Concentration of the conjugate base, [A⁻] (mol/L)",
  targetPh: "Target pH",
  totalConcentration: "Total buffer concentration, [HA] + [A⁻] (mol/L)",
  totalOptional: "Total buffer concentration (mol/L, optional)",
  volumeLabel: "Volume of buffer to make (mL)",
  acidFormula: "Formula of the acid form to weigh out",
  baseFormula: "Formula of the base form to weigh out",
  formulaHint: "Hydrates work too, such as NaH2PO4·H2O.",
  results: "Results",
  pkaShown: "pKa",
  phShown: "Buffer pH",
  ratioShown: "[A⁻] ÷ [HA]",
  exactShown: "pH without the approximation",
  baseShare: "Conjugate base",
  acidShare: "Acid",
  columnComponent: "Component",
  columnMoles: "Amount (mol)",
  columnMass: "Mass (g)",
  columnConcentration: "Concentration (mol/L)",
  columnFormula: "Formula",
  componentAcid: "Acid form",
  componentBase: "Base form",
  enter: "Enter the values above to see the result.",
  badValue: "Enter numbers greater than zero.",
  badPka:
    "Choose a weak acid or enter its Ka or pK as a number greater than zero.",
  bufferRangeYes:
    "The ratio is within 0.1 to 10, the range in which the buffer works well: neither component is less than about a tenth of the other.",
  bufferRangeNo:
    "The ratio is outside 0.1 to 10. The pH is more than one unit from the pKa, so this is a weak buffer: it resists added acid or base poorly in one direction.",
  holdsYes:
    "The approximation agrees with the exact calculation to within 0.05 pH units for this buffer.",
  holdsNo:
    "The approximation is off by {difference} pH units here, because the concentrations are too low or the ratio too extreme for the starting amounts to stand in for the equilibrium ones. Trust the exact value.",
  workingHeading: "The working",
  phLine:
    "pH = pKa + log₁₀([A⁻] ÷ [HA]) = {pka} + log₁₀({base} ÷ {acid}) = {ph}",
  ratioLine: "[A⁻] ÷ [HA] = 10^(pH − pKa) = 10^({ph} − {pka}) = {ratio}",
  shareLine: "[A⁻] makes up {base} % of the buffer and [HA] {acid} %",
  recipeMoleLine: "{formula}: {moles} mol × {molar} g/mol = {mass} g",
  recipeTotalLine:
    "Total amount = {total} mol/L × {volume} mL × 10⁻³ L/mL = {moles} mol",
  recipeInstructions:
    "Dissolve {acid} g of {acidFormula} and {base} g of {baseFormula} in less than {volume} mL of water, then make it up to {volume} mL.",
  actualPh:
    "Check: with these amounts the exact pH is {exact}, against a target of {target}.",
  formulaErrors: FORMULA_ERROR_STRINGS,
  copy: "Copy result",
  copied: "Copied!",
  copyPh: "Buffer pH = {ph}",
  copyRatio: "[A⁻] ÷ [HA] = {ratio} for pH {ph}",
  copyRecipe:
    "{acid} g {acidFormula} + {base} g {baseFormula} → {volume} mL, pH {ph}",
} as const;

type Strings = typeof HENDERSON_HASSELBALCH_CALCULATOR_STRINGS;
type Mode = "ph" | "ratio" | "recipe";

const DEFAULT_PH_DECIMALS = 2;
const DEFAULT_SIG = 3;
const ML_TO_L = 1e-3;
const PERCENT = 100;

function parsePositive(raw: string): number | null {
  const value = parseMeasuredNumber(raw);
  return value !== null && value > 0 ? value : null;
}

interface PairState {
  constant: ConstantState;
  acidFormula: string;
  baseFormula: string;
}

function pairFor(id: string): BufferPair | undefined {
  return BUFFER_PAIRS.find((pair) => pair.id === id);
}

function Verdicts({
  ratio,
  approximate,
  exact,
  strings,
  fmt,
}: {
  ratio: number;
  approximate: number;
  exact: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const holds = approximationHolds(approximate, exact);
  return (
    <>
      <Notice>
        {inBufferRange(ratio) ? strings.bufferRangeYes : strings.bufferRangeNo}
      </Notice>
      <Notice>
        {holds
          ? strings.holdsYes
          : t(strings.holdsNo, {
              difference: formatFixed(fmt, Math.abs(approximate - exact), 2),
            })}
      </Notice>
    </>
  );
}

function PhMode({
  pKa,
  k,
  strings,
  fmt,
}: {
  pKa: number;
  k: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [acid, setAcid] = useState("0.10");
  const [base, setBase] = useState("0.10");
  const a = parsePositive(acid);
  const b = parsePositive(base);
  let body = <ErrorNotice>{strings.badValue}</ErrorNotice>;
  if (a !== null && b !== null) {
    const ph = bufferPh(pKa, a, b);
    const exact = exactBufferPh(k, a, b);
    const text = formatFixed(fmt, ph, DEFAULT_PH_DECIMALS);
    body = (
      <>
        <StatGrid>
          <StatCard emphasis label={strings.phShown} value={text} />
          <StatCard
            label={strings.ratioShown}
            value={formatSig(fmt, b / a, DEFAULT_SIG)}
          />
          <StatCard
            label={strings.exactShown}
            value={formatFixed(fmt, exact, DEFAULT_PH_DECIMALS)}
          />
        </StatGrid>
        <Verdicts
          approximate={ph}
          exact={exact}
          fmt={fmt}
          ratio={b / a}
          strings={strings}
        />
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <div className={WORKING_BOX}>
            <p>
              {t(strings.phLine, {
                pka: formatFixed(fmt, pKa, DEFAULT_PH_DECIMALS),
                base: formatSig(fmt, b, DEFAULT_SIG),
                acid: formatSig(fmt, a, DEFAULT_SIG),
                ph: text,
              })}
            </p>
          </div>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copyPh, { ph: text })}
        />
      </>
    );
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="hh-acid"
          label={strings.acidConcentration}
          numeric
          onChange={setAcid}
          value={acid}
        />
        <TextField
          id="hh-base"
          label={strings.baseConcentration}
          numeric
          onChange={setBase}
          value={base}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function RatioMode({
  pKa,
  strings,
  fmt,
}: {
  pKa: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [target, setTarget] = useState("5.00");
  const [total, setTotal] = useState("");
  const ph = parseMeasuredNumber(target);
  const sum = total.trim() === "" ? null : parsePositive(total);
  let body = <ErrorNotice>{strings.badValue}</ErrorNotice>;
  if (ph !== null && (total.trim() === "" || sum !== null)) {
    const ratio = ratioForPh(pKa, ph);
    const split = shares(ratio);
    const pkaText = formatFixed(fmt, pKa, DEFAULT_PH_DECIMALS);
    const ratioText = formatSig(fmt, ratio, DEFAULT_SIG);
    body = (
      <>
        <StatGrid>
          <StatCard emphasis label={strings.ratioShown} value={ratioText} />
          <StatCard
            label={strings.baseShare}
            value={`${formatSig(fmt, split.base * PERCENT, DEFAULT_SIG)} %`}
          />
          <StatCard
            label={strings.acidShare}
            value={`${formatSig(fmt, split.acid * PERCENT, DEFAULT_SIG)} %`}
          />
        </StatGrid>
        {sum === null ? null : (
          <DataTable
            head={[strings.columnComponent, strings.columnConcentration]}
            rows={[
              [
                strings.componentAcid,
                formatSig(fmt, sum * split.acid, DEFAULT_SIG),
              ],
              [
                strings.componentBase,
                formatSig(fmt, sum * split.base, DEFAULT_SIG),
              ],
            ]}
          />
        )}
        <Notice>
          {inBufferRange(ratio)
            ? strings.bufferRangeYes
            : strings.bufferRangeNo}
        </Notice>
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <div className={WORKING_BOX}>
            <p>
              {t(strings.ratioLine, {
                ph: formatFixed(fmt, ph, DEFAULT_PH_DECIMALS),
                pka: pkaText,
                ratio: ratioText,
              })}
            </p>
            <p>
              {t(strings.shareLine, {
                base: formatSig(fmt, split.base * PERCENT, DEFAULT_SIG),
                acid: formatSig(fmt, split.acid * PERCENT, DEFAULT_SIG),
              })}
            </p>
          </div>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copyRatio, {
            ratio: ratioText,
            ph: formatFixed(fmt, ph, DEFAULT_PH_DECIMALS),
          })}
        />
      </>
    );
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="hh-target"
          label={strings.targetPh}
          numeric
          onChange={setTarget}
          value={target}
        />
        <TextField
          id="hh-total-optional"
          label={strings.totalOptional}
          numeric
          onChange={setTotal}
          value={total}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function RecipeMode({
  pKa,
  k,
  pair,
  onFormula,
  strings,
  fmt,
}: {
  pKa: number;
  k: number;
  pair: PairState;
  onFormula: (patch: Partial<PairState>) => void;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [target, setTarget] = useState("5.00");
  const [total, setTotal] = useState("0.100");
  const [volume, setVolume] = useState("500");
  const ph = parseMeasuredNumber(target);
  const c = parsePositive(total);
  const v = parsePositive(volume);
  const acidParsed = parseFormula(pair.acidFormula);
  const baseParsed = parseFormula(pair.baseFormula);

  let body: ReactNode = <ErrorNotice>{strings.badValue}</ErrorNotice>;
  if (!acidParsed.ok) {
    body = (
      <ErrorNotice>
        {formulaErrorMessage(strings.formulaErrors, acidParsed.error)}
      </ErrorNotice>
    );
  } else if (!baseParsed.ok) {
    body = (
      <ErrorNotice>
        {formulaErrorMessage(strings.formulaErrors, baseParsed.error)}
      </ErrorNotice>
    );
  } else if (ph !== null && c !== null && v !== null) {
    const recipe = bufferRecipe(pKa, ph, c, v * ML_TO_L);
    const sig = fewestSigFigs([total], DEFAULT_SIG);
    const acidMass = molarMass(acidParsed.formula).total;
    const baseMass = molarMass(baseParsed.formula).total;
    const acidGrams = recipe.acidMoles * acidMass;
    const baseGrams = recipe.baseMoles * baseMass;
    const exact = exactBufferPh(
      k,
      recipe.acidConcentration,
      recipe.baseConcentration
    );
    const targetText = formatFixed(fmt, ph, DEFAULT_PH_DECIMALS);
    body = (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={`${strings.componentAcid}: ${pair.acidFormula}`}
            value={`${formatSig(fmt, acidGrams, sig)} g`}
          />
          <StatCard
            label={`${strings.componentBase}: ${pair.baseFormula}`}
            value={`${formatSig(fmt, baseGrams, sig)} g`}
          />
          <StatCard
            label={strings.ratioShown}
            value={formatSig(fmt, recipe.ratio, DEFAULT_SIG)}
          />
        </StatGrid>
        <DataTable
          head={[
            strings.columnComponent,
            strings.columnFormula,
            strings.columnMoles,
            strings.columnMass,
          ]}
          rows={[
            [
              strings.componentAcid,
              pair.acidFormula,
              formatSig(fmt, recipe.acidMoles, sig),
              formatSig(fmt, acidGrams, sig),
            ],
            [
              strings.componentBase,
              pair.baseFormula,
              formatSig(fmt, recipe.baseMoles, sig),
              formatSig(fmt, baseGrams, sig),
            ],
          ]}
        />
        <Notice>
          {t(strings.recipeInstructions, {
            acid: formatSig(fmt, acidGrams, sig),
            base: formatSig(fmt, baseGrams, sig),
            acidFormula: pair.acidFormula,
            baseFormula: pair.baseFormula,
            volume: fmt(v),
          })}
        </Notice>
        <Notice>
          {t(strings.actualPh, {
            exact: formatFixed(fmt, exact, DEFAULT_PH_DECIMALS),
            target: targetText,
          })}
        </Notice>
        <Verdicts
          approximate={ph}
          exact={exact}
          fmt={fmt}
          ratio={recipe.ratio}
          strings={strings}
        />
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <div className={WORKING_BOX}>
            <p>
              {t(strings.ratioLine, {
                ph: targetText,
                pka: formatFixed(fmt, pKa, DEFAULT_PH_DECIMALS),
                ratio: formatSig(fmt, recipe.ratio, sig + 1),
              })}
            </p>
            <p>
              {t(strings.recipeTotalLine, {
                total: formatSig(fmt, c, sig),
                volume: fmt(v),
                moles: formatSig(fmt, c * v * ML_TO_L, sig + 1),
              })}
            </p>
            <p>
              {t(strings.recipeMoleLine, {
                formula: pair.acidFormula,
                moles: formatSig(fmt, recipe.acidMoles, sig + 1),
                molar: formatSig(fmt, acidMass, sig + 1),
                mass: formatSig(fmt, acidGrams, sig),
              })}
            </p>
            <p>
              {t(strings.recipeMoleLine, {
                formula: pair.baseFormula,
                moles: formatSig(fmt, recipe.baseMoles, sig + 1),
                molar: formatSig(fmt, baseMass, sig + 1),
                mass: formatSig(fmt, baseGrams, sig),
              })}
            </p>
          </div>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copyRecipe, {
            acid: formatSig(fmt, acidGrams, sig),
            base: formatSig(fmt, baseGrams, sig),
            acidFormula: pair.acidFormula,
            baseFormula: pair.baseFormula,
            volume: fmt(v),
            ph: targetText,
          })}
        />
      </>
    );
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="hh-recipe-target"
          label={strings.targetPh}
          numeric
          onChange={setTarget}
          value={target}
        />
        <TextField
          id="hh-recipe-total"
          label={strings.totalConcentration}
          numeric
          onChange={setTotal}
          value={total}
        />
        <TextField
          id="hh-recipe-volume"
          label={strings.volumeLabel}
          numeric
          onChange={setVolume}
          value={volume}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.formulaHint}
          id="hh-acid-formula"
          label={strings.acidFormula}
          mono
          onChange={(acidFormula) => onFormula({ acidFormula })}
          value={pair.acidFormula}
        />
        <TextField
          id="hh-base-formula"
          label={strings.baseFormula}
          mono
          onChange={(baseFormula) => onFormula({ baseFormula })}
          value={pair.baseFormula}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

export default function HendersonHasselbalchCalculatorWidget() {
  const strings = useToolUi(HENDERSON_HASSELBALCH_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("ph");
  const first = BUFFER_PAIRS[0];
  const [pair, setPair] = useState<PairState>({
    constant: initialConstant(first),
    acidFormula: first.acidFormula,
    baseFormula: first.baseFormula,
  });
  const k = readConstant(pair.constant, BUFFER_PAIRS);
  const pKa = k === null ? null : pFrom(k);

  function changeConstant(constant: ConstantState) {
    const chosen = pairFor(constant.preset);
    setPair(
      chosen
        ? {
            constant,
            acidFormula: chosen.acidFormula,
            baseFormula: chosen.baseFormula,
          }
        : { ...pair, constant }
    );
  }

  return (
    <>
      <WeakConstantFields
        constants={BUFFER_PAIRS}
        idPrefix="hh"
        names={strings.pairNames}
        onChange={changeConstant}
        presetLabel={strings.pairLabel}
        side="acid"
        state={pair.constant}
        strings={strings}
      />
      <PillGroup
        label={strings.modeLabel}
        name="hh-mode"
        onChange={setMode}
        options={[
          { value: "ph", label: strings.modePh },
          { value: "ratio", label: strings.modeRatio },
          { value: "recipe", label: strings.modeRecipe },
        ]}
        value={mode}
      />
      {k === null || pKa === null ? (
        <ErrorNotice>{strings.badPka}</ErrorNotice>
      ) : (
        <>
          {mode === "ph" ? (
            <PhMode fmt={fmt} k={k} pKa={pKa} strings={strings} />
          ) : null}
          {mode === "ratio" ? (
            <RatioMode fmt={fmt} pKa={pKa} strings={strings} />
          ) : null}
          {mode === "recipe" ? (
            <RecipeMode
              fmt={fmt}
              k={k}
              onFormula={(patch) => setPair({ ...pair, ...patch })}
              pair={pair}
              pKa={pKa}
              strings={strings}
            />
          ) : null}
        </>
      )}
    </>
  );
}
